/**
 * WooCommerce Integration Factory
 * Factory class for creating and configuring WooCommerce integration instances
 */

import { WooCommerceIntegration, WooCommerceConfig } from './WooCommerceIntegration';
import { IntegrationConfig } from '../../../types/integration';

export interface WooCommerceConnectionParams {
  siteUrl: string;
  consumerKey: string;
  consumerSecret: string;
  apiVersion?: string;
  webhookSecret?: string;
  webhookEndpoint?: string;
  useSSL?: boolean;
  timeout?: number;
}

export class WooCommerceIntegrationFactory {
  
  /**
   * Create a new WooCommerce integration instance
   */
  static create(params: WooCommerceConnectionParams): WooCommerceIntegration {
    const config: WooCommerceConfig = {
      id: `woocommerce_${this.extractDomainFromUrl(params.siteUrl)}`,
      name: `WooCommerce - ${this.extractDomainFromUrl(params.siteUrl)}`,
      type: 'ecommerce',
      platform: 'woocommerce',
      status: 'inactive',
      siteUrl: params.siteUrl,
      consumerKey: params.consumerKey,
      consumerSecret: params.consumerSecret,
      apiVersion: params.apiVersion || 'v3',
      webhookSecret: params.webhookSecret,
      webhookEndpoint: params.webhookEndpoint,
      useSSL: params.useSSL !== false, // Default to true
      timeout: params.timeout || 30000,
      credentials: {
        encrypted: '',
        keyId: '',
        algorithm: 'aes-256-gcm',
        createdAt: new Date()
      },
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 100,
        rateLimitPerMinute: 60,
        retryAttempts: 3,
        timeoutMs: params.timeout || 30000,
        customSettings: {}
      },
      fieldMappings: [],
      syncSchedule: {
        enabled: true,
        frequency: 'real_time',
        timezone: 'UTC',
        batchSize: 100,
        conflictResolution: 'manual'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'unhealthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 0,
        issues: [{
          type: 'info',
          message: 'Not yet connected',
          timestamp: new Date(),
          resolved: false
        }]
      },
      metadata: {
        description: 'WooCommerce e-commerce integration',
        tags: ['ecommerce', 'woocommerce', 'wordpress', 'products', 'customers', 'orders'],
        category: 'E-commerce',
        version: '1.0.0',
        supportedFeatures: [
          'product_sync',
          'customer_sync',
          'order_sync',
          'cart_abandonment',
          'inventory_tracking',
          'webhooks',
          'product_recommendations'
        ],
        limitations: [
          'Rate limited based on hosting provider',
          'Webhook delivery depends on WordPress cron',
          'Some features require additional plugins'
        ],
        documentation: 'https://woocommerce.github.io/woocommerce-rest-api-docs/',
        customData: {}
      }
    };

    return new WooCommerceIntegration(config);
  }

  /**
   * Create WooCommerce integration from existing configuration
   */
  static fromConfig(config: IntegrationConfig): WooCommerceIntegration {
    if (config.platform !== 'woocommerce') {
      throw new Error('Invalid platform for WooCommerce integration');
    }

    return new WooCommerceIntegration(config as WooCommerceConfig);
  }

  /**
   * Validate WooCommerce connection parameters
   */
  static validateParams(params: WooCommerceConnectionParams): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate site URL
    if (!params.siteUrl) {
      errors.push('Site URL is required');
    } else if (!this.isValidUrl(params.siteUrl)) {
      errors.push('Invalid site URL format');
    }

    // Validate consumer key
    if (!params.consumerKey) {
      errors.push('Consumer key is required');
    } else if (params.consumerKey.length < 10) {
      errors.push('Consumer key appears to be too short');
    }

    // Validate consumer secret
    if (!params.consumerSecret) {
      errors.push('Consumer secret is required');
    } else if (params.consumerSecret.length < 10) {
      errors.push('Consumer secret appears to be too short');
    }

    // Validate API version
    if (params.apiVersion && !this.isValidApiVersion(params.apiVersion)) {
      errors.push('Invalid API version format (should be v1, v2, or v3)');
    }

    // Validate webhook endpoint if provided
    if (params.webhookEndpoint && !this.isValidUrl(params.webhookEndpoint)) {
      errors.push('Invalid webhook endpoint URL format');
    }

    // Validate timeout
    if (params.timeout && (params.timeout < 1000 || params.timeout > 300000)) {
      errors.push('Timeout must be between 1000ms and 300000ms (5 minutes)');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get default configuration for WooCommerce integration
   */
  static getDefaultConfig(): Partial<WooCommerceConfig> {
    return {
      apiVersion: 'v3',
      useSSL: true,
      timeout: 30000,
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 100,
        rateLimitPerMinute: 60,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {}
      },
      syncSchedule: {
        enabled: true,
        frequency: 'real_time',
        timezone: 'UTC',
        batchSize: 100,
        conflictResolution: 'manual'
      }
    };
  }

  /**
   * Get required permissions for different features
   */
  static getRequiredPermissions(features: string[]): string[] {
    const permissionMap: Record<string, string[]> = {
      products: ['read', 'write'],
      customers: ['read', 'write'],
      orders: ['read', 'write'],
      coupons: ['read', 'write'],
      reports: ['read'],
      webhooks: ['read', 'write'],
      system_status: ['read'],
      settings: ['read', 'write'],
      shipping: ['read', 'write'],
      taxes: ['read', 'write']
    };

    const requiredPermissions = new Set<string>();
    
    for (const feature of features) {
      const permissions = permissionMap[feature];
      if (permissions) {
        permissions.forEach(permission => requiredPermissions.add(`${feature}:${permission}`));
      }
    }

    return Array.from(requiredPermissions);
  }

  /**
   * Generate WooCommerce REST API credentials URL
   */
  static generateCredentialsUrl(siteUrl: string, appName: string = 'Email Marketing Platform'): string {
    const cleanUrl = siteUrl.replace(/\/$/, ''); // Remove trailing slash
    const encodedAppName = encodeURIComponent(appName);
    
    return `${cleanUrl}/wp-admin/admin.php?page=wc-settings&tab=advanced&section=keys&create-key=1&app_name=${encodedAppName}`;
  }

  /**
   * Test WooCommerce site accessibility
   */
  static async testSiteAccessibility(siteUrl: string): Promise<{
    accessible: boolean;
    isWordPress: boolean;
    isWooCommerce: boolean;
    version?: string;
    error?: string;
  }> {
    try {
      // Test basic site accessibility
      const response = await fetch(siteUrl, {
        method: 'HEAD'
      });

      if (!response.ok) {
        return {
          accessible: false,
          isWordPress: false,
          isWooCommerce: false,
          error: `Site returned ${response.status} ${response.statusText}`
        };
      }

      // Check if it's a WordPress site
      const isWordPress = response.headers.get('x-powered-by')?.includes('WordPress') ||
                         response.headers.get('server')?.includes('WordPress') ||
                         response.headers.get('link')?.includes('wp-json');

      // Try to access WooCommerce REST API endpoint
      let isWooCommerce = false;
      let version: string | undefined;

      try {
        const apiUrl = `${siteUrl.replace(/\/$/, '')}/wp-json/wc/v3/system_status`;
        const apiResponse = await fetch(apiUrl, {
          method: 'HEAD'
        });

        if (apiResponse.status === 401 || apiResponse.status === 403) {
          // Authentication required - this means WooCommerce API is available
          isWooCommerce = true;
        } else if (apiResponse.ok) {
          isWooCommerce = true;
        }
      } catch (error) {
        // API endpoint not accessible, but site might still have WooCommerce
      }

      return {
        accessible: true,
        isWordPress,
        isWooCommerce,
        version
      };
    } catch (error) {
      return {
        accessible: false,
        isWordPress: false,
        isWooCommerce: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get WooCommerce system requirements
   */
  static getSystemRequirements(): {
    wordpress: string;
    php: string;
    mysql: string;
    requiredPlugins: string[];
    recommendedPlugins: string[];
  } {
    return {
      wordpress: '5.0+',
      php: '7.4+',
      mysql: '5.6+',
      requiredPlugins: [
        'WooCommerce'
      ],
      recommendedPlugins: [
        'WooCommerce REST API',
        'WooCommerce Webhooks',
        'WooCommerce Analytics',
        'WooCommerce Customer/Order/Coupon Export'
      ]
    };
  }

  /**
   * Generate setup instructions for WooCommerce integration
   */
  static getSetupInstructions(): {
    steps: Array<{
      title: string;
      description: string;
      action?: string;
    }>;
    troubleshooting: Array<{
      issue: string;
      solution: string;
    }>;
  } {
    return {
      steps: [
        {
          title: 'Install WooCommerce',
          description: 'Ensure WooCommerce plugin is installed and activated on your WordPress site',
          action: 'Go to WordPress Admin → Plugins → Add New → Search for "WooCommerce"'
        },
        {
          title: 'Enable REST API',
          description: 'Make sure the WooCommerce REST API is enabled',
          action: 'Go to WooCommerce → Settings → Advanced → REST API'
        },
        {
          title: 'Create API Keys',
          description: 'Generate consumer key and secret for API access',
          action: 'Go to WooCommerce → Settings → Advanced → REST API → Add Key'
        },
        {
          title: 'Set Permissions',
          description: 'Grant Read/Write permissions for products, orders, and customers',
          action: 'Select "Read/Write" permissions when creating the API key'
        },
        {
          title: 'Configure Webhooks (Optional)',
          description: 'Set up webhooks for real-time data synchronization',
          action: 'Go to WooCommerce → Settings → Advanced → Webhooks'
        },
        {
          title: 'Test Connection',
          description: 'Use the generated credentials to test the integration',
          action: 'Enter your site URL, consumer key, and consumer secret'
        }
      ],
      troubleshooting: [
        {
          issue: 'Authentication failed',
          solution: 'Verify consumer key and secret are correct and have proper permissions'
        },
        {
          issue: 'SSL certificate errors',
          solution: 'Ensure your site has a valid SSL certificate or disable SSL verification'
        },
        {
          issue: 'Rate limiting errors',
          solution: 'Reduce sync frequency or contact your hosting provider about rate limits'
        },
        {
          issue: 'Webhook delivery failures',
          solution: 'Check that your webhook endpoint is accessible and WordPress cron is working'
        },
        {
          issue: 'Missing products/orders',
          solution: 'Verify API permissions include read access to products and orders'
        }
      ]
    };
  }

  // Private validation methods

  private static isValidUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private static isValidApiVersion(version: string): boolean {
    return ['v1', 'v2', 'v3'].includes(version);
  }

  private static extractDomainFromUrl(url: string): string {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.hostname.replace('www.', '');
    } catch {
      return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace('www.', '');
    }
  }
}